import React from "react";
import { Box, Typography } from "@mui/material";
import "./LabeledField.scss";

interface LabeledFieldProps {
  controlId: string;
  label: string;
  required?: boolean;
  badge?: string;
  className?: string;
  error?: string;
  hint?: string;
  /** Colours the hint: green for a good result, orange for an advisory flag. Plain when absent. */
  hintTone?: "success" | "warning";
  children: React.ReactNode;
}

/**
 * Shared labeled-control shell for forms that sit under the app theme.
 * Keeps the visible required marker out of the accessible name so labels remain predictable.
 */
const LabeledField: React.FC<LabeledFieldProps> = ({
  controlId,
  label,
  required = false,
  badge,
  className,
  error,
  hint,
  hintTone,
  children,
}) => (
  <Box className={`labeled-field${className ? ` ${className}` : ""}`}>
    <Box className="labeled-field__head">
      <Typography
        component="label"
        htmlFor={controlId}
        id={`${controlId}-label`}
        className="labeled-field__label"
      >
        {label}
        {required && (
          <>
            {" "}
            <span className="labeled-field__required" aria-hidden="true">
              *
            </span>
          </>
        )}
      </Typography>
      {badge && <span className="labeled-field__badge">{badge}</span>}
    </Box>
    {children}
    {error ? (
      <Typography variant="caption" className="labeled-field__error">
        {error}
      </Typography>
    ) : (
      hint && (
        <Typography
          variant="caption"
          className={`labeled-field__hint${hintTone ? ` labeled-field__hint--${hintTone}` : ""}`}
        >
          {hint}
        </Typography>
      )
    )}
  </Box>
);

export default LabeledField;
